import React, { useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import type { Stat } from '../lib/types';

interface StatsProps {
  stats: Stat[];
  className?: string;
}

const Stats: React.FC<StatsProps> = ({ stats, className = '' }) => {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 ${className}`}>
      {stats.map((stat, index) => (
        <StatItem key={index} stat={stat} index={index} />
      ))}
    </div>
  );
};

interface StatItemProps {
  stat: Stat;
  index: number;
}

const StatItem: React.FC<StatItemProps> = ({ stat, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    const numericValue = parseInt(stat.value.replace(/[^0-9]/g, ''));
    if (isNaN(numericValue)) return;

    const duration = 2000;
    const steps = 60;
    const increment = numericValue / steps;
    const stepDuration = duration / steps;

    let currentCount = 0;
    const timer = setInterval(() => {
      currentCount += increment;
      if (currentCount >= numericValue) {
        setCount(numericValue);
        clearInterval(timer);
      } else {
        setCount(Math.floor(currentCount));
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [isInView, stat.value]);

  const formatValue = (value: number) => {
    if (stat.value.includes('K')) return `${(value / 1000).toFixed(1)}K`;
    if (stat.value.includes('M')) return `${(value / 1000000).toFixed(1)}M`;
    return value.toLocaleString();
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="text-center"
    >
      <div className="stat-number">
        {stat.value.includes('$') && '$'}
        {formatValue(count)}
        {stat.suffix || stat.value.match(/[+%]/)?.[0] || ''}
      </div>
      <div className="stat-label mt-2">{stat.label}</div>
    </motion.div>
  );
};

export default Stats;
